import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, Package, Download, Calendar, CreditCard, Lock, Unlock,
  Image, Video, Eye, ChevronRight, Star, Shield, Diamond, Award,
  Sparkles, Gift, Heart, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import FadeImage from '../components/FadeImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Discord invite for Gold members
const DISCORD_INVITE_URL = "https://discord.gg/Nw3rFE4G";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState([]);
  const [bundleContent, setBundleContent] = useState({});
  const [packageConfig, setPackageConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [contentDialog, setContentDialog] = useState(false);
  const [vaultDialog, setVaultDialog] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      // Fetch all package types first
      const packagesRes = await axios.get(`${API}/packages/all-types`);
      const allPackages = packagesRes.data;
      
      // Merge with default config for icons
      const defaultConfig = {
        bronze: { name: 'Bronze', icon: '🥉', color: '#CD7F32', photos: 5, videos: 0 },
        silver: { name: 'Silver', icon: '🥈', color: '#C0C0C0', photos: 10, videos: 2 },
        gold: { name: 'Gold', icon: '🥇', color: '#D4AF37', photos: 20, videos: 5 },
        platinum: { name: 'Platinum', icon: '💎', color: '#E5E4E2', photos: 50, videos: 10 },
        daily_tease: { name: 'Daily Tease', icon: '☕', color: '#D4AF37', photos: 30, videos: 0 },
        midnight_crave: { name: 'Midnight Crave', icon: '🍺', color: '#D4AF37', photos: 90, videos: 10 },
        pure_obsession: { name: 'Pure Obsession', icon: '🎬', color: '#D4AF37', photos: 'Unlimited', videos: 'Unlimited' },
        gold_vip: { name: 'Gold VIP', icon: '👑', color: '#D4AF37', photos: 'Unlimited', videos: 'Unlimited' },
      };
      
      setPackageConfig({ ...defaultConfig, ...allPackages });
      
      // Fetch purchases
      const purchasesRes = await axios.get(`${API}/purchases`);
      setPurchases(purchasesRes.data);
      
      // Fetch user's content preview (FAST - only 3 items per package)
      try {
        const previewRes = await axios.get(`${API}/my-content-preview`);
        const preview = previewRes.data;
        
        // Build content map from the by_package data
        const contentMap = {};
        if (preview.by_package) {
          Object.keys(preview.by_package).forEach(pkgId => {
            const pkgContent = preview.by_package[pkgId];
            // Map media items for display
            const combined = (pkgContent.media || []).map(m => ({
              ...m,
              type: 'media',
              preview_url: m.url,
              image_url: m.url,
              title: m.filename || 'Media',
              total_count: pkgContent.total_count || 0
            }));
            contentMap[pkgId] = combined;
          });
        }
        setBundleContent(contentMap);
      } catch (contentErr) {
        console.error('Failed to fetch my-content-preview:', contentErr);
        // Fallback to bundle-content endpoint
        const contentPromises = purchasesRes.data.slice(0, 10).map(async (p) => {
          try {
            const res = await axios.get(`${API}/bundle-content/${p.package_id}`);
            return { package_id: p.package_id, content: res.data.slice(0, 3) };
          } catch {
            return { package_id: p.package_id, content: [] };
          }
        });
        
        const contentResults = await Promise.all(contentPromises);
        const contentMap = {};
        contentResults.forEach(r => {
          contentMap[r.package_id] = r.content;
        });
        setBundleContent(contentMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate loyalty level based on total spent
  const totalSpent = purchases.reduce((acc, p) => acc + (p.amount || 0), 0);
  const getLoyaltyLevel = () => {
    if (totalSpent >= 500) return { level: 'diamond', name: 'Diamond', icon: Diamond, color: '#B9F2FF', discount: 20 };
    if (totalSpent >= 150) return { level: 'gold', name: 'Gold', icon: Crown, color: '#D4AF37', discount: 10 };
    if (totalSpent >= 50) return { level: 'silver', name: 'Silver', icon: Shield, color: '#C0C0C0', discount: 5 };
    return { level: 'bronze', name: 'Bronze', icon: Award, color: '#CD7F32', discount: 0 };
  };
  const loyalty = getLoyaltyLevel();
  const LoyaltyIcon = loyalty.icon;

  // Check if user has access to Secret Vault (Diamond members)
  const hasVaultAccess = loyalty.level === 'diamond' || user?.is_admin;
  
  // Check if user has Discord access (Gold+)
  const hasDiscordAccess = ['gold', 'diamond'].includes(loyalty.level) || user?.is_admin;

  const openContentViewer = (packageId) => {
    setSelectedPackage(packageId);
    setContentDialog(true);
  };

  const downloadContent = async (item) => {
    try {
      // Check if it's a PDF
      const isPdf = item.filename?.toLowerCase().endsWith('.pdf') || 
                    item.content_type?.includes('pdf') ||
                    item.title?.toLowerCase().includes('pdf') ||
                    item.title?.toLowerCase().includes('guide');
      
      // Get the content URL
      const contentUrl = item.cloud_url || item.image_url || item.url || item.preview_url;
      
      if (!contentUrl) {
        toast.error('Download niet beschikbaar');
        return;
      }
      
      // For PDFs or when we need authenticated download
      if (isPdf || contentUrl.includes('/api/')) {
        const token = localStorage.getItem('token');
        const response = await axios.get(contentUrl, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Create blob URL and download
        const blob = new Blob([response.data], { 
          type: isPdf ? 'application/pdf' : response.headers['content-type'] 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = item.filename || item.title || (isPdf ? 'ella-gold-guide.pdf' : 'ella-gold-content');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(blobUrl);
      } else {
        // Direct download for external URLs
        const link = document.createElement('a');
        link.href = contentUrl;
        link.download = item.filename || item.title || 'ella-gold-content';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success('Download gestart!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download mislukt');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalPhotos = purchases.reduce((acc, p) => {
    const pkg = packageConfig[p.package_id];
    return acc + (typeof pkg?.photos === 'number' ? pkg.photos : 100);
  }, 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Crown className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Dashboard</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mb-3">
            Welkom, <span className="brand-gold text-5xl">{user?.name}</span>
          </h1>
          <p className="text-gray-400">Bekijk en download je gekochte content</p>
        </div>

        {/* Loyalty Status Card */}
        <div className="mb-8 p-6 glass rounded-2xl border border-white/10 sparkle-border" style={{ borderColor: loyalty.color + '50' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: loyalty.color + '20' }}
              >
                <LoyaltyIcon className="w-8 h-8" style={{ color: loyalty.color }} />
              </div>
              <div>
                <p className="text-sm text-gray-400">Jouw status</p>
                <h3 className="text-2xl font-bold" style={{ color: loyalty.color }}>{loyalty.name} Member</h3>
                <p className="text-sm text-gray-400">
                  €{totalSpent.toFixed(0)} besteed • {loyalty.discount}% korting
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              {hasDiscordAccess && (
                <a 
                  href={DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#5865F2]/20 text-[#5865F2] rounded-xl hover:bg-[#5865F2]/30 transition-all"
                >
                  <ExternalLink className="w-4 h-4" />
                  Discord VIP
                </a>
              )}
              {hasVaultAccess && (
                <Button 
                  onClick={() => setVaultDialog(true)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Diamond className="w-4 h-4 mr-2" />
                  Secret Vault
                </Button>
              )}
            </div>
          </div>
          
          {/* Progress to next level */}
          {loyalty.level !== 'diamond' && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Naar volgende level</span>
                <span className="text-[#D4AF37]">
                  {loyalty.level === 'bronze' && `€${50 - totalSpent} tot Silver`}
                  {loyalty.level === 'silver' && `€${150 - totalSpent} tot Gold`}
                  {loyalty.level === 'gold' && `€${500 - totalSpent} tot Diamond`}
                </span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FCD34D] rounded-full transition-all"
                  style={{ 
                    width: `${
                      loyalty.level === 'bronze' ? (totalSpent / 50) * 100 :
                      loyalty.level === 'silver' ? ((totalSpent - 50) / 100) * 100 :
                      ((totalSpent - 150) / 350) * 100
                    }%`
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">{purchases.length}</p>
                <p className="text-xs text-gray-400">Aankopen</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Image className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">{totalPhotos}</p>
                <p className="text-xs text-gray-400">Foto's</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Video className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">
                  {purchases.reduce((acc, p) => acc + (packageConfig[p.package_id]?.videos || 0), 0)}
                </p>
                <p className="text-xs text-gray-400">Video's</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold gold-text">€{totalSpent.toFixed(0)}</p>
                <p className="text-xs text-gray-400">Totaal</p>
              </div>
            </div>
          </div>
        </div>

        {/* My Purchases / Content */}
        <div className="mb-8">
          <h2 className="text-2xl font-medium mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            Mijn Content
          </h2>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-48 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : purchases.length === 0 ? (
            <div className="text-center py-16 glass rounded-xl">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium mb-2">Nog geen aankopen</h3>
              <p className="text-gray-400 mb-6">Je hebt nog geen pakketten gekocht</p>
              <Button 
                onClick={() => navigate('/packages')}
                className="btn-gold px-8"
                data-testid="buy-first-package"
              >
                Bekijk Pakketten
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {purchases.map((purchase, index) => {
                const pkg = packageConfig[purchase.package_id];
                const content = bundleContent[purchase.package_id] || [];
                return (
                  <div 
                    key={purchase.id || index}
                    className="glass rounded-xl overflow-hidden group cursor-pointer hover:border-[#D4AF37]/50 border border-transparent transition-all"
                    onClick={() => openContentViewer(purchase.package_id)}
                    data-testid={`purchase-${index}`}
                  >
                    {/* Preview Images */}
                    <div className="grid grid-cols-3 h-32">
                      {content.slice(0, 3).map((item, i) => (
                        <div key={i} className="relative overflow-hidden">
                          <FadeImage 
                            src={item.preview_url || item.image_url}
                            alt=""
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ))}
                      {content.length === 0 && (
                        <div className="col-span-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
                          <Gift className="w-12 h-12 text-[#D4AF37]/50" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{pkg?.icon || '📦'}</span>
                          <h3 className="font-medium">{pkg?.name || purchase.package_id}</h3>
                        </div>
                        <span 
                          className="px-2 py-1 text-xs rounded-full"
                          style={{ backgroundColor: pkg?.color + '20', color: pkg?.color }}
                        >
                          {content.length} items
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {new Date(purchase.created_at).toLocaleDateString('nl-NL')}
                        </span>
                        <span className="text-[#D4AF37] flex items-center gap-1 group-hover:gap-2 transition-all">
                          <Eye className="w-4 h-4" />
                          Bekijken
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upgrade CTA */}
        {purchases.length > 0 && (
          <div className="p-8 glass rounded-2xl text-center sparkle-border">
            <h3 className="text-2xl font-medium mb-3">
              Wil je <span className="brand-gold text-3xl">meer</span> content?
            </h3>
            <p className="text-gray-400 mb-6">
              Upgrade naar een groter pakket voor nog meer exclusieve content
            </p>
            <Button 
              onClick={() => navigate('/packages')}
              className="btn-gold px-8"
              data-testid="upgrade-btn"
            >
              Bekijk Pakketten
            </Button>
          </div>
        )}
      </div>

      {/* Content Viewer Dialog */}
      <Dialog open={contentDialog} onOpenChange={setContentDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <span className="text-2xl">{packageConfig[selectedPackage]?.icon}</span>
              {packageConfig[selectedPackage]?.name} Content
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] mt-4">
            {(bundleContent[selectedPackage] || []).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Gift className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Nog geen content beschikbaar</p>
                <p className="text-sm mt-2">De admin voegt binnenkort content toe</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {(bundleContent[selectedPackage] || []).map((item, index) => {
                  const isVideo = item.content_type?.includes('video') || 
                                  item.type === 'media' && item.preview_url?.includes('/media/') &&
                                  (item.filename?.endsWith('.mp4') || item.filename?.endsWith('.webm') || item.filename?.endsWith('.mov'));
                  
                  return (
                    <div key={index} className="group relative rounded-xl overflow-hidden bg-black/30">
                      {isVideo ? (
                        <video 
                          src={item.preview_url || item.image_url || `${API}/media/${item.id}`}
                          className="w-full aspect-[3/4] object-cover"
                          controls
                          preload="metadata"
                          playsInline
                          webkit-playsinline="true"
                          x5-playsinline="true"
                          controlsList="nodownload"
                          poster={item.thumbnail_url || ''}
                          style={{ 
                            maxWidth: '100%',
                            minHeight: '200px'
                          }}
                        >
                          <source src={item.preview_url || item.image_url || `${API}/media/${item.id}`} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <>
                          <FadeImage 
                            src={item.preview_url || item.image_url}
                            alt={item.title}
                            className="w-full aspect-[3/4] object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                            <p className="text-sm font-medium truncate mb-2">{item.title}</p>
                            <Button 
                              onClick={(e) => { e.stopPropagation(); downloadContent(item); }}
                              size="sm"
                              className="w-full btn-gold"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </>
                      )}
                      {isVideo && (
                        <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          Video
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Secret Vault Dialog */}
      <Dialog open={vaultDialog} onOpenChange={setVaultDialog}>
        <DialogContent className="bg-[#0A0A0A] border-purple-500/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Diamond className="w-6 h-6 text-purple-400" />
              Secret Vault
              <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                DIAMOND ONLY
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="text-center py-12 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20">
              <Diamond className="w-16 h-16 mx-auto mb-4 text-purple-400" />
              <h3 className="text-xl font-medium mb-2">Welkom in de Secret Vault</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Als Diamond member heb je toegang tot de meest exclusieve content. 
                Nieuwe items worden wekelijks toegevoegd.
              </p>
              <div className="flex justify-center gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-400">∞</p>
                  <p className="text-xs text-gray-400">Exclusieve content</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-400">VIP</p>
                  <p className="text-xs text-gray-400">Prioriteit support</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-400">20%</p>
                  <p className="text-xs text-gray-400">Korting altijd</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Vault content wordt binnenkort toegevoegd door de admin
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
